import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SharedService } from '@app/services/shared.service';
import { AddFundsModel } from '@app/models/add-funds.model';
import { MessageModel } from '@app/models/message.model';
import { RememberCardPaymentFundsModel } from '@app/models/remembered-card-payment-funds.model';
import { VerificationModel } from '@app/models/verification.model';
import { AddFundsPayPalModel } from '@app/models/add-funds-paypal.model';
import { SortDirection } from '@app/directives/sortable.directive';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, switchMap, delay } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { VerificationByEmailModel } from '@app/models/verification-by-email.model';

interface SearchResult {
  messages: any[];
  total: number;
}

interface State {
  page: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: SortDirection;
}

function compare(v1, v2) {
  return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
}

function sort(countries: any[], column: string, direction: string): any[] {
  if (direction === '') {
    return countries;
  } else {
    return [...countries].sort((a, b) => {
      const res = compare(a[column], b[column]);
      return direction === 'ASC' ? res : -res;
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class MessageService extends SharedService {

  private _search$ = new Subject<void>();
  private _messages$ = new BehaviorSubject<any[]>([]);
  private _total$ = new BehaviorSubject<number>(0);


  private _state: State = {
    page: 1,
    pageSize: 10,
    sortColumn: '',
    sortDirection: ''
  };

  constructor(
    httpClient: HttpClient,
    private toastr: ToastrService
  ) {
    super(httpClient);
    this._search$.pipe(
      debounceTime(200),
      switchMap(() => this._search()),
      delay(200),
    ).subscribe(result => {
      this._messages$.next(result.messages);
      this._total$.next(result.total);
    });

    this._search$.next();
  }

  get contacts$() { return this._messages$.asObservable(); }
  get total$() { return this._total$.asObservable(); }
  get page() { return this._state.page; }
  get pageSize() { return this._state.pageSize; }
  get state() { return this._state; }

  set page(page: number) { this._set({ page }); }
  set pageSize(pageSize: number) { this._set({ pageSize }); }
  set sortColumn(sortColumn: string) { this._set({ sortColumn }); }
  set sortDirection(sortDirection: SortDirection) { this._set({ sortDirection }); }
  set state(state: State) { this._set(state); }

  private _set(patch: Partial<State>) {
    Object.assign(this._state, patch);
    this._search$.next();
  }

  private _search(): Promise<SearchResult> | Observable<SearchResult> {
    if (this.state.sortColumn !== '') {
      const { sortColumn, sortDirection, pageSize, page } = this._state;
      return new Promise((resolve, reject) => {
        Promise.all([
          this.getPaginatedMesages(pageSize.toString(), page.toString(), sortColumn, sortDirection)
        ]).then(
          (result: any) => {
            const messages = sort(result[0].messages, sortColumn, sortDirection);
            const total = result[0].totalCount;
            resolve({ messages, total });
          }, reject);
      });
    }
    return new Observable();

  }

  addFunds(addFundsModel: AddFundsModel): Promise<any> {
    const url = 'api/message/AddFunds';
    return this.postItmes(url, addFundsModel);
  }

  addTokenBasedFunds(rememberCardPaymentFundsModel: RememberCardPaymentFundsModel) {
    const url = 'api/message/AddTokenBasedFunds';
    return this.postItmes(url, rememberCardPaymentFundsModel);
  }

  addFundsPayPal(fundsPayPal: AddFundsPayPalModel) {
    const url = 'api/message/AddFundsPayPal';
    return this.postItmes(url, fundsPayPal);
  }

  verifyAmount(verificationModel: VerificationModel) {
    const url = 'api/Message/VerifyAmount';
    return this.postItmes(url, verificationModel);
  }

  verifyByEmail(verificationModel: VerificationByEmailModel) {
    const url = 'api/Message/Verify';
    return this.postItmes(url, verificationModel);
  }

  verifyCancel(token: any) {
    const url = 'api/Message/Cancel';
    return this.postItmes(url, token);
  }

  sendSms(message: MessageModel) {
    const url = 'api/message/sendsms';
    return this.postItmes(url, message);
  }

  getMessages() {
    const url = 'api/message/GetAvailableMessages';
    return this.getItems(url);
  }

  getPaginatedMesages(pageSize: string, pageNumber: string, orderBy: string, order: string) {
    const url = 'api/TopUp/GetMessages';
    return this.getPaginatedItems(url, pageSize, pageNumber, orderBy, order);
  }

  getPriceForMessages() {
    const url = 'api/TopUp/GetPrices?st=3&sn=true';
    return this.getItems(url);
  }

  deleteMessage(id: string) {
    const url = `api/message/DeleteMessage?id=${id}`;
    this.loadingSubject.next(true);
    return this.postItmes(url, {});
  }


}
